import React, { useState, useEffect } from 'react';
import { Layout, Card, Select, Row, Col, Statistic, Timeline, Progress, Badge, Alert, Space } from 'antd';
import { CheckCircleOutlined, ClockCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import DataTable from '../components/Tables/DataTable';
import type { ColumnsType } from 'antd/es/table';
import { checkRDFoxStatus } from '../utils/rdfoxUtils';
import getDataDecompositionObserved from '../api/Test/DecompositionObservedRequest';
import getDataDecompositionResidual from '../api/Test/DecompositionResidualRequest';
import getCombinedData from '../api/UC1/Senario2Request';

// Types for data
interface TableData {
  useCase1: any[];
  useCase2: any[];
  useCase3: any[];
}

interface WorkflowStatus {
  id: string;
  name: string;
  status: 'active' | 'completed' | 'error';
  progress: number;
  startTime: string;
}

interface ServiceStatus {
  name: string;
  status: 'operational' | 'warning' | 'error';
  uptime: number;
}

const { Content } = Layout;
const { Option } = Select;

const Dashboard: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [tableData, setTableData] = useState<TableData>({ useCase1: [], useCase2: [], useCase3: [] });
  const [activeWorkflows, setActiveWorkflows] = useState<WorkflowStatus[]>([]);
  const [services, setServices] = useState<ServiceStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedTable, setSelectedTable] = useState<'useCase1' | 'useCase2' | 'useCase3'>('useCase1');

  // Check RDFox availability and load data
  useEffect(() => {
    const checkRDFoxAndLoadData = async () => {
      setLoading(true);
      try {
        await checkRDFoxStatus();
        await loadData();
      } catch (err) {
        setError('Failed to connect to RDFox or load data');
      } finally {
        setLoading(false);
      }
    };

    checkRDFoxAndLoadData();
  }, []);

  const loadData = async () => {
    try {
      const [useCase1Data, observedData, residualData] = await Promise.all([
        getCombinedData(),
        getDataDecompositionObserved(),
        getDataDecompositionResidual()
      ]);

      // Transform data to match the expected format
      const formattedData = {
        useCase1: useCase1Data.map(item => ({
          key: item.id,
          id: item.id,
          date: new Date().toLocaleString(),
          usage: (item.upload || 0) + (item.download || 0),
          cluster: item.claster || 0
        })),
        useCase2: observedData.map(item => ({
          key: item.id,
          id: item.id,
          TimeWindow: new Date(item.TimeWindow).toLocaleString(),
          TotalTraffic: Number(item.TotalTraffic.toFixed(2))
        })),
        useCase3: residualData.map(item => ({
          key: item.id,
          id: item.id,
          TimeWindow: new Date(item.TimeWindow).toLocaleString(),
          resid: Number(item.resid.toFixed(4))
        }))
      };

      setTableData(formattedData);

      // Load workflow and service status data
      const mockWorkflows: WorkflowStatus[] = [
        {
          id: '1',
          name: 'Data Processing Pipeline',
          status: 'active',
          progress: 75,
          startTime: new Date().toISOString()
        },
        {
          id: '2',
          name: 'Analysis Workflow',
          status: 'completed',
          progress: 100,
          startTime: new Date(Date.now() - 3600000).toISOString()
        }
      ];

      const mockServices: ServiceStatus[] = [
        {
          name: 'RDFox Database',
          status: 'operational',
          uptime: 99.9
        },
        { name: 'Processing Engine', status: 'operational', uptime: 99.5 },
        { name: 'API Gateway', status: 'operational', uptime: 99.9 },
      ];

      setActiveWorkflows(mockWorkflows);
      setServices(mockServices);
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data from RDFox. Please ensure the service is running.');
    }
  };

  // Style for main container
  const containerStyle: React.CSSProperties = {
    overflowY: 'auto',
    padding: '24px'
  };

  const useCase1Columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      sorter: (a, b) => a.id.localeCompare(b.id)
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '30%',
      sorter: (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
    },
    {
      title: 'Usage (KBytes)',
      dataIndex: 'usage',
      key: 'usage',
      width: '25%',
      sorter: (a, b) => a.usage - b.usage
    },
    {
      title: 'Cluster',
      dataIndex: 'cluster',
      key: 'cluster',
      width: '25%',
      sorter: (a, b) => a.cluster - b.cluster
    }
  ];

  const useCase2Columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      sorter: (a, b) => a.id.localeCompare(b.id)
    },
    {
      title: 'Time Window',
      dataIndex: 'TimeWindow',
      key: 'TimeWindow',
      width: '40%',
      sorter: (a, b) => new Date(a.TimeWindow).getTime() - new Date(b.TimeWindow).getTime()
    },
    {
      title: 'Total Traffic (KBytes)',
      dataIndex: 'TotalTraffic',
      key: 'TotalTraffic',
      width: '40%',
      sorter: (a, b) => a.TotalTraffic - b.TotalTraffic,
      render: (value: number) => value.toLocaleString()
    }
  ];

  const useCase3Columns: ColumnsType<any> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: '20%',
      sorter: (a, b) => a.id.localeCompare(b.id)
    },
    {
      title: 'Time Window',
      dataIndex: 'TimeWindow',
      key: 'TimeWindow',
      width: '40%',
      sorter: (a, b) => new Date(a.TimeWindow).getTime() - new Date(b.TimeWindow).getTime()
    },
    {
      title: 'Residual',
      dataIndex: 'resid',
      key: 'resid',
      width: '40%',
      sorter: (a, b) => a.resid - b.resid,
      render: (value: number) => value.toLocaleString()
    }
  ];

  const renderServiceStatus = (status: ServiceStatus) => {
    const statusColor = {
      operational: '#52c41a',
      warning: '#faad14',
      error: '#f5222d'
    };

    return (
      <Badge
        color={statusColor[status.status]}
        text={`${status.name} (${status.uptime}% uptime)`}
      />
    );
  };

  return (
    <Layout style={{ background: '#f0f2f5' }}>
      <Content style={containerStyle}>
        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '20px' }}
          />
        )}

        {/* Section 1: Main View - General Status */}
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Card title="Active Workflows">
              <Statistic
                title="Total Workflows"
                value={activeWorkflows.length}
                prefix={<ClockCircleOutlined />}
              />
              {activeWorkflows.map(workflow => (
                <div key={workflow.id} style={{ marginTop: 16 }}>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <span>{workflow.name}</span>
                    <Progress percent={workflow.progress} status={workflow.status === 'error' ? 'exception' : undefined} />
                  </Space>
                </div>
              ))}
            </Card>
          </Col>
          <Col span={12}>
            <Card title="Services Status">
              <Space direction="vertical" style={{ width: '100%' }}>
                {services.map(service => (
                  <div key={service.name}>
                    {renderServiceStatus(service)}
                  </div>
                ))}
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Section 2: Key Metrics */}
        <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
          <Col span={8}>
            <Card>
              <Statistic
                title="Success Rate"
                value={98.7}
                suffix="%"
                prefix={<CheckCircleOutlined />}
                valueStyle={{ color: '#3f8600' }}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Average Processing Time"
                value={2.3}
                suffix="s"
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col span={8}>
            <Card>
              <Statistic
                title="Active Alerts"
                value={2}
                prefix={<ExclamationCircleOutlined />}
                valueStyle={{ color: '#cf1322' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Section 3: Data Table */}
        <Card style={{ marginTop: 16 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              style={{ width: 200 }}
              value={selectedTable}
              onChange={setSelectedTable}
            >
              <Option value="useCase1">Use Case 1</Option>
              <Option value="useCase2">Decomposition Observed</Option>
              <Option value="useCase3">Decomposition Residual</Option>
            </Select>
            <DataTable
              loading={loading}
              columns={
                selectedTable === 'useCase1' ? useCase1Columns :
                  selectedTable === 'useCase2' ? useCase2Columns :
                    useCase3Columns
              }
              data={tableData[selectedTable]}
            />
          </Space>
        </Card>

        {/* Section 4: Latest Events */}
        <Card style={{ marginTop: 16 }} title="Latest Events">
          <Timeline
            items={[
              { color: "green", children: "Workflow \"Data Processing\" completed successfully" },
              { color: "yellow", children: "Warning: High CPU Usage" },
              { children: "New workflow started: \"Analysis Pipeline\"" },
              { color: "red", children: "Error: Database connection failed" }
            ]}
          />
        </Card>
      </Content>
    </Layout>
  );
};

export default Dashboard;